export const isObject = (val: unknown): val is object =>
  val != null && typeof val === 'object'
export const isArray = (val: unknown): val is unknown[] => Array.isArray(val)
export const isString = (val: unknown): val is string => typeof val === 'string'
export const isNumber = (val: unknown): val is number => typeof val === 'number'
export const isBoolean = (val: unknown): val is boolean =>
  typeof val === 'boolean'
export const isNumberString = (val: unknown): val is string | number =>
  typeof val === 'number' || (typeof val === 'string' && !isNaN(Number(val)))
export const isValues =
  <const T>(...values: T[]) =>
  (val: unknown): val is T =>
    values.includes(val as T)
export const isArrayOf =
  <T>(pred: (v: unknown) => v is T) =>
  (val: unknown): val is T[] =>
    Array.isArray(val) && val.every(pred)
export const keyIs = <K extends string, T>(
  val: object,
  key: K,
  pred: (v: unknown) => v is T
): val is object & { [key in K]: T } =>
  key in val && pred(val[key as keyof typeof val])
export const keyAs = <K extends string, T>(
  val: object,
  key: K,
  pred: (v: unknown) => T
) => (key in val ? pred(val[key as keyof typeof val]) : undefined)

export const notNull = <T>(value: T): value is Exclude<T, null | undefined> =>
  value != null
type Falsy = null | undefined | false | 0 | 0n | ''
export const notFalsy = <const T>(value: T): value is Exclude<T, Falsy> =>
  Boolean(value)

export function asArrayOf<T>(
  value: unknown,
  func: (value: unknown) => value is T
): T[] {
  if (!Array.isArray(value)) return []
  return value.filter(func)
}
export function asObjectOf<T>(
  value: unknown,
  func: (value: unknown) => value is T
): Record<string, T> {
  const result: Record<string, T> = {}
  if (value && typeof value === 'object') {
    for (const [key, val] of Object.entries(value)) {
      if (func(val)) result[key] = val
    }
  }
  return result
}

export function filterObject<V>(
  src: object,
  val: (v: unknown) => V | undefined,
  key?: (k: string) => string | undefined
) {
  const result: Record<string, V> = {}
  for (const [k, v] of Object.entries(src)) {
    const kv = key ? key(k) : k
    if (kv === undefined) continue
    const tv = val(v)
    if (tv !== undefined) result[kv] = tv
  }
  return result
}
export function filterObjectNumeric<V>(
  src: object,
  val: (v: unknown) => V | undefined,
  key?: (k: number) => number | undefined
) {
  const result: Record<number, V> = {}
  for (const [k, v] of Object.entries(src)) {
    const ki = Number(k)
    if (isNaN(ki)) continue
    const kv = key ? key(ki) : ki
    if (kv === undefined) continue
    const tv = val(v)
    if (tv !== undefined) result[kv] = tv
  }
  return result
}
export function filterArray<V>(
  src: unknown[],
  val: (v: unknown, index: number) => V | undefined
) {
  const result: V[] = []
  for (const v of src) {
    const tv = val(v, result.length)
    if (tv !== undefined) result.push(tv)
  }
  return result
}

export function removeUndefined<T extends {}>(obj: T) {
  const result: any = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) result[k] = v
  }
  return result as {
    [key in keyof T]: T[key] extends infer V | undefined ? V : never
  }
}
